'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import supabase from '@/supabase';
import { RealtimeChannel } from '@supabase/supabase-js';
import { getBotAvatarSrc } from '@/components/bots/getBotAvatarSrc';

export interface ConversationListItem {
  id: string;
  updated_at: string;
  conversation_type: 'direct' | 'bot';
  navigation_path: string;
  other_participant: {
    id: string;
    display_name: string | null;
    avatar_url: string | null;
  };
  last_message: {
    id: string;
    content: string | null;
    attachment_type: 'image' | 'video' | 'audio' | 'file' | null;
    created_at: string;
    sender_id: string;
  } | null;
  unread_count: number;
  last_read_at: string | null;
  awaiting_response: boolean;
}

interface UseConversationListProps {
  userId: string;
  enabled?: boolean;
}

interface UseConversationListReturn {
  conversations: ConversationListItem[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useConversationList({
  userId,
  enabled = true,
}: UseConversationListProps): UseConversationListReturn {
  const [conversations, setConversations] = useState<ConversationListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const channelRef = useRef<RealtimeChannel | null>(null);

  const fetchConversations = useCallback(async () => {
    if (!userId || !enabled) {
      setConversations([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Fetch conversation IDs where user is a participant
      const { data: participantData, error: participantError } = await supabase
        .from('conversation_participants')
        .select('conversation_id, last_read_at')
        .eq('user_id', userId);

      if (participantError) {
        console.error('[useConversationList] Error fetching participant data:', participantError);
        setError('Failed to load conversations');
        return;
      }

      const conversationIds = (participantData || []).map((p) => p.conversation_id);
      const lastReadMap = new Map((participantData || []).map((p) => [p.conversation_id, p.last_read_at]));

      if (conversationIds.length > 0) {
        const { data: conversationsData, error: conversationsError } = await supabase
          .from('conversations')
          .select('id, updated_at')
          .in('id', conversationIds)
          .order('updated_at', { ascending: false });

        if (conversationsError) {
          console.error('[useConversationList] Error fetching conversations:', conversationsError);
          setError('Failed to load conversations');
          return;
        }

        const { data: allParticipants, error: allParticipantsError } = await supabase
          .from('conversation_participants')
          .select(`
            conversation_id,
            user_id,
            profile:profiles!conversation_participants_user_id_fkey(
              id,
              display_name,
              avatar_url
            )
          `)
          .in('conversation_id', conversationIds)
          .neq('user_id', userId);

        if (allParticipantsError) {
          console.error('[useConversationList] Error fetching participants:', allParticipantsError);
          setError('Failed to load conversations');
          return;
        }

        const otherParticipantMap = new Map<string, { id: string; display_name: string | null; avatar_url: string | null }>();
        for (const participant of allParticipants || []) {
          const profile = participant.profile as
            | { id: string; display_name: string | null; avatar_url: string | null }
            | { id: string; display_name: string | null; avatar_url: string | null }[]
            | null;
          const profileData = Array.isArray(profile) ? profile[0] : profile;

          if (profileData) {
            otherParticipantMap.set(participant.conversation_id, {
              id: profileData.id,
              display_name: profileData.display_name,
              avatar_url: profileData.avatar_url,
            });
          }
        }

        const lastMessagesResults = await Promise.all(
          conversationIds.map(async (conversationId) => {
            const { data, error: msgError } = await supabase
              .from('direct_messages')
              .select('id, content, attachment_type, created_at, sender_id')
              .eq('conversation_id', conversationId)
              .order('created_at', { ascending: false })
              .limit(1)
              .maybeSingle();

            if (msgError) {
              console.error(`[useConversationList] Error fetching last message for ${conversationId}:`, msgError);
              return { conversationId, message: null };
            }

            return { conversationId, message: data };
          })
        );

        const lastMessageMap = new Map(lastMessagesResults.map((result) => [result.conversationId, result.message]));

        const unreadCountResults = await Promise.all(
          conversationIds.map(async (conversationId) => {
            const lastReadAt = lastReadMap.get(conversationId);

            let query = supabase
              .from('direct_messages')
              .select('id', { count: 'exact', head: true })
              .eq('conversation_id', conversationId)
              .neq('sender_id', userId);

            if (lastReadAt) {
              query = query.gt('created_at', lastReadAt);
            }

            const { count, error: countError } = await query;

            if (countError) {
              console.error(`[useConversationList] Error fetching unread count for ${conversationId}:`, countError);
              return { conversationId, count: 0 };
            }

            return { conversationId, count: count || 0 };
          })
        );

        const unreadCountMap = new Map(unreadCountResults.map((result) => [result.conversationId, result.count]));

        const directConversations = (conversationsData || []).map((conv): ConversationListItem | null => {
          const otherParticipant = otherParticipantMap.get(conv.id);
          if (!otherParticipant) return null;

          const lastMessage = lastMessageMap.get(conv.id);
          const unreadCount = unreadCountMap.get(conv.id) || 0;
          const lastReadAt = lastReadMap.get(conv.id) || null;

          return {
            id: conv.id,
            updated_at: conv.updated_at,
            conversation_type: 'direct',
            navigation_path: `/chat/${conv.id}`,
            other_participant: otherParticipant,
            last_message: lastMessage
              ? {
                  id: lastMessage.id,
                  content: lastMessage.content,
                  attachment_type: lastMessage.attachment_type,
                  created_at: lastMessage.created_at,
                  sender_id: lastMessage.sender_id,
                }
              : null,
            unread_count: unreadCount,
            last_read_at: lastReadAt,
            awaiting_response: lastMessage?.sender_id === userId,
          };
        });

        directConversations
          .filter((conv): conv is ConversationListItem => conv !== null);
      }

      const { data: botConversationsData, error: botConversationsError } = await supabase
        .from('bot_conversations')
        .select(`
          id,
          bot_id,
          updated_at,
          created_at,
          bot:bots!bot_conversations_bot_id_fkey(
            id,
            name,
            avatar_url
          )
        `)
        .eq('user_id', userId)
        .order('updated_at', { ascending: false });

      if (botConversationsError) {
        console.error('[useConversationList] Error fetching bot conversations:', botConversationsError);
        setError('Failed to load conversations');
        return;
      }

      const botConversationIds = (botConversationsData || []).map((conversation) => conversation.id);
      const botLastMessageResults = await Promise.all(
        botConversationIds.map(async (conversationId) => {
          const { data, error: msgError } = await supabase
            .from('bot_messages')
            .select('id, content, created_at, role')
            .eq('conversation_id', conversationId)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (msgError) {
            console.error(`[useConversationList] Error fetching last bot message for ${conversationId}:`, msgError);
            return { conversationId, message: null };
          }

          return { conversationId, message: data };
        })
      );

      const botLastMessageMap = new Map(
        botLastMessageResults.map((result) => [result.conversationId, result.message])
      );

      const botConversations = (botConversationsData || []).map((conversation): ConversationListItem | null => {
          const bot = Array.isArray(conversation.bot) ? conversation.bot[0] : conversation.bot;
          if (!bot) return null;

          const lastMessage = botLastMessageMap.get(conversation.id);

          return {
            id: conversation.id,
            updated_at: conversation.updated_at || conversation.created_at,
            conversation_type: 'bot',
            navigation_path: `/bot/${bot.id}`,
            other_participant: {
              id: bot.id,
              display_name: bot.name,
              avatar_url: getBotAvatarSrc({
                id: bot.id,
                name: bot.name,
                avatar_url: bot.avatar_url,
              }) || null,
            },
            last_message: lastMessage
              ? {
                  id: lastMessage.id,
                  content: lastMessage.content,
                  attachment_type: null,
                  created_at: lastMessage.created_at,
                  sender_id: lastMessage.role === 'user' ? userId : bot.id,
                }
              : null,
            unread_count: 0,
            last_read_at: null,
            awaiting_response: lastMessage?.role === 'user',
          };
        });

      const botConversationList = botConversations
        .filter((conv): conv is ConversationListItem => conv !== null);

      setConversations(
        botConversationList.sort(
          (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
        )
      );
    } catch (err) {
      console.error('[useConversationList] Unexpected error:', err);
      setError('Failed to load conversations');
    } finally {
      setIsLoading(false);
    }
  }, [userId, enabled]);

  // Initial fetch
  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Subscribe to realtime updates for new messages and conversation changes
  useEffect(() => {
    if (!userId || !enabled) return;

    const channelName = `conversation-list:${userId}`;

    // Clean up existing channel
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
    }

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'conversations',
        },
        () => {
          fetchConversations();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'direct_messages',
        },
        () => {
          // Refetch conversations when a new message arrives
          fetchConversations();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'conversations',
        },
        () => {
          // Refetch when conversation is updated
          fetchConversations();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'conversation_participants',
          filter: `user_id=eq.${userId}`,
        },
        () => {
          fetchConversations();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'bot_conversations',
          filter: `user_id=eq.${userId}`,
        },
        () => {
          fetchConversations();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'bot_conversations',
          filter: `user_id=eq.${userId}`,
        },
        () => {
          fetchConversations();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'bot_messages',
        },
        () => {
          fetchConversations();
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [userId, enabled, fetchConversations]);

  return {
    conversations,
    isLoading,
    error,
    refetch: fetchConversations,
  };
}
